import { useEffect, useMemo, useRef, useState } from 'react';
import {
  exportPackageEnquiriesCsv,
  getPackageEnquiry,
  listPackageEnquiries,
  listPackages,
  updatePackageEnquiry,
} from '../../api/adminPackages';
import { useToast } from '../../components/ToastContext';
import { io } from 'socket.io-client';

const STATUSES = ['NEW', 'CONTACTED', 'CONFIRMED', 'CLOSED'];

const badgeTone = (status) => {
  const value = (status || '').toUpperCase();
  if (value === 'CONFIRMED') return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (value === 'CONTACTED') return 'border-amber-100 bg-amber-50 text-amber-700';
  if (value === 'CLOSED') return 'border-slate-200 bg-slate-100 text-slate-700';
  return 'border-sky-100 bg-sky-50 text-sky-700';
};

const formatDate = (value, options) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', options || { day: 'numeric', month: 'short', year: 'numeric' });
};

const extractPagination = (meta = {}, fallbackPage = 1, fallbackLimit = 20) => ({
  page: Number(meta.page || fallbackPage),
  limit: Number(meta.pageSize || meta.limit || fallbackLimit),
  total: Number(meta.total || 0),
  unreadCount: Number(meta.unreadCount || 0),
});

const AdminPackageEnquiries = () => {
  const toast = useToast();
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    read: '',
    packageId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [items, setItems] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, unreadCount: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('NEW');
  const loadRef = useRef(null);

  const totalPages = useMemo(() => {
    const total = Number(pagination.total || 0);
    const limit = Number(pagination.limit || 20);
    return total ? Math.ceil(total / limit) : 1;
  }, [pagination.limit, pagination.total]);

  const load = async () => {
    setLoading(true);
    try {
      const readParam =
        filters.read === 'read' ? true : filters.read === 'unread' ? false : undefined;

      const res = await listPackageEnquiries({
        status: filters.status || undefined,
        search: filters.search || undefined,
        read: readParam,
        packageId: filters.packageId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });

      const data = res?.data || res || [];
      const meta = res?.meta || {};
      setItems(data);
      setPagination((prev) => ({
        ...prev,
        ...extractPagination(meta, prev.page, prev.limit),
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to load package enquiries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    let active = true;
    listPackages({ pageSize: 100 })
      .then((res) => {
        if (!active) return;
        setPackageOptions(res?.data || res || []);
      })
      .catch(() => {
        if (!active) return;
        setPackageOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.read, filters.search, filters.packageId, filters.dateFrom, filters.dateTo, pagination.page, pagination.limit]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden && loadRef.current) loadRef.current();
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const socket = io(apiBase, { transports: ['websocket'], withCredentials: true });
    socket.on('enquiry_created', () => {
      if (loadRef.current) loadRef.current();
    });
    return () => {
      socket.off('enquiry_created');
      socket.disconnect();
    };
  }, []);

  const openEnquiry = async (id) => {
    try {
      const res = await getPackageEnquiry(id);
      const data = res?.data || res || null;
      setSelected(data);
      setNotesDraft(data?.admin_notes || '');
      setStatusDraft(data?.status || 'NEW');
      if (data && data.is_read !== true) {
        await updateReadState(id, true, false);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load enquiry details.');
    }
  };

  const updateReadState = async (id, isRead, shouldToast = true) => {
    try {
      const res = await updatePackageEnquiry(id, { isRead });
      const updated = res?.data || res || null;
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, is_read: isRead, read_at: updated?.read_at || (isRead ? new Date().toISOString() : null) }
            : item
        )
      );
      setSelected((prev) =>
        prev && prev.id === id
          ? { ...prev, is_read: isRead, read_at: updated?.read_at || (isRead ? new Date().toISOString() : null) }
          : prev
      );
      setPagination((prev) => ({
        ...prev,
        unreadCount: Math.max(0, prev.unreadCount + (isRead ? -1 : 1)),
      }));
      if (shouldToast) toast.success(isRead ? 'Marked as read.' : 'Marked as unread.');
    } catch (err) {
      toast.error(err.message || 'Failed to update read state.');
    }
  };

  const saveUpdates = async () => {
    if (!selected) return;
    try {
      const res = await updatePackageEnquiry(selected.id, {
        status: statusDraft,
        adminNotes: notesDraft,
      });
      const updated = res?.data || res || null;
      setSelected(updated);
      setItems((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      toast.success('Enquiry updated.');
    } catch (err) {
      toast.error(err.message || 'Failed to update enquiry.');
    }
  };

  const exportCsv = async () => {
    try {
      const readParam =
        filters.read === 'read' ? true : filters.read === 'unread' ? false : undefined;
      const csvText = await exportPackageEnquiriesCsv({
        status: filters.status || undefined,
        search: filters.search || undefined,
        read: readParam,
        packageId: filters.packageId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `package-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Failed to export CSV.');
    }
  };

  const selectedPackage = packageOptions.find((item) => String(item.id) === String(filters.packageId));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-2xl font-semibold text-slate-900">Package Enquiries</h1>
          <p className="mt-1 text-sm text-slate-500">
            Track enquiries from package cards and detail pages. {pagination.unreadCount} unread of{' '}
            {pagination.total || 0} total.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="rounded-full border border-amber-100 bg-amber-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
            {pagination.unreadCount} new enquiries
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm xl:grid-cols-6">
        <input
          type="search"
          placeholder="Search name, email, phone, or package"
          value={filters.search}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, search: event.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') load();
          }}
          className="rounded-2xl border border-slate-200 px-4 py-3 text-sm xl:col-span-2"
        />
        <select
          value={filters.read}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, read: event.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
        >
          <option value="">Read: All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
        <select
          value={filters.status}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, status: event.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
        >
          <option value="">Status: All</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        <select
          value={filters.packageId}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, packageId: event.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
        >
          <option value="">Package: All</option>
          {packageOptions.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
        <div className="flex items-center justify-end gap-2 text-xs text-slate-500">
          <span>Rows</span>
          <select
            value={pagination.limit}
            onChange={(event) => setPagination((prev) => ({ ...prev, limit: Number(event.target.value), page: 1 }))}
            className="rounded-full border border-slate-200 px-3 py-2 text-xs"
          >
            {[10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, dateFrom: event.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, dateTo: event.target.value }));
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
        />
        <button
          type="button"
          onClick={() => {
            setFilters({
              status: '',
              search: '',
              read: '',
              packageId: '',
              dateFrom: '',
              dateTo: '',
            });
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-full border border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600"
        >
          Clear filters
        </button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.35fr_0.95fr]">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <tr>
                  <th className="px-4 py-4">Date</th>
                  <th className="px-4 py-4">Package</th>
                  <th className="px-4 py-4">Customer</th>
                  <th className="px-4 py-4">Travel</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Source</th>
                  <th className="px-4 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-sm text-slate-500">
                      Loading enquiries...
                    </td>
                  </tr>
                ) : null}
                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                      No enquiries matched the current filters.
                    </td>
                  </tr>
                ) : null}
                {!loading
                  ? items.map((item) => {
                      const unread = !item.is_read;
                      return (
                        <tr key={item.id} className={unread ? 'bg-amber-50/40' : 'bg-white'}>
                          <td className="px-4 py-4 text-xs text-slate-500">
                            {formatDate(item.created_at)}
                            <div className="mt-1 text-[11px] text-slate-400">
                              {new Date(item.created_at).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2 font-semibold text-slate-900">
                              {unread ? <span className="h-2 w-2 rounded-full bg-rose-500" /> : null}
                              <span>{item.package_title}</span>
                            </div>
                            <div className="mt-1 text-xs text-slate-500">{item.package_id ? `ID ${item.package_id}` : '-'}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-semibold text-slate-900">{item.name || item.full_name}</div>
                            <div className="mt-1 text-xs text-slate-500">{item.email}</div>
                            <div className="mt-1 text-xs text-slate-500">{item.phone || '-'}</div>
                          </td>
                          <td className="px-4 py-4 text-xs text-slate-600">
                            <div>{formatDate(item.travel_start_date || item.travel_date || item.preferred_month)}</div>
                            <div className="mt-1 text-slate-400">{item.travellers_count || item.travellers || '-'} travellers</div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${badgeTone(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-xs uppercase tracking-[0.16em] text-slate-500">
                            {item.source || '-'}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => openEnquiry(item.id)}
                              className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  : null}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-4 text-xs text-slate-500">
            <span>
              Page {pagination.page} of {totalPages}
              {selectedPackage ? ` â€¢ ${selectedPackage.title}` : ''}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                className="rounded-full border border-slate-200 px-4 py-2 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={pagination.page >= totalPages}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                className="rounded-full border border-slate-200 px-4 py-2 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
          {!selected ? (
            <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
              Select an enquiry to view customer details, notes, and status actions.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Selected enquiry</p>
                  <h2 className="mt-2 text-xl font-semibold text-slate-900">{selected.package_title}</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {selected.name || selected.full_name} â€¢ {selected.email}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => updateReadState(selected.id, !selected.is_read)}
                    className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700"
                  >
                    {selected.is_read ? 'Mark unread' : 'Mark read'}
                  </button>
                  <select
                    value={statusDraft}
                    onChange={(event) => setStatusDraft(event.target.value)}
                    className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em]"
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-3 rounded-[24px] border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Customer</p>
                  <p className="mt-1 font-semibold text-slate-900">{selected.name || selected.full_name}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Phone</p>
                  <p className="mt-1 font-semibold text-slate-900">{selected.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Travel date</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {formatDate(selected.travel_start_date || selected.travel_date || selected.preferred_month)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Travellers</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {selected.travellers_count || selected.travellers || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Budget</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {selected.budget || (selected.budget_min || selected.budget_max
                      ? `${selected.budget_min || '-'} - ${selected.budget_max || '-'}`
                      : '-')}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Source</p>
                  <p className="mt-1 font-semibold text-slate-900">{selected.source || '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Message</p>
                <div className="mt-2 rounded-[24px] border border-slate-100 bg-slate-50 p-4 text-sm leading-7 text-slate-700">
                  {selected.message || 'No message was provided.'}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Admin notes</p>
                <textarea
                  rows={5}
                  className="mt-2 w-full rounded-[24px] border border-slate-200 px-4 py-3 text-sm"
                  value={notesDraft}
                  onChange={(event) => setNotesDraft(event.target.value)}
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={saveUpdates}
                  className="rounded-full bg-slate-900 px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white"
                >
                  Save updates
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPackageEnquiries;


